import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toaster } from 'evergreen-ui';
import api from '../../../services/api';
import { useUser } from '../../../context/UserContext';

export const useEntityOverview = (currentProject, markAsSeen) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userContext = useUser();
  const currentUser = userContext?.currentUser || null;
  
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entityOverview, setEntityOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [entityDetails, setEntityDetails] = useState(null);
  const [sidePanelTab, setSidePanelTab] = useState('overview');
  const [wstgChecklist, setWstgChecklist] = useState(null);
  const [wstgLoading, setWstgLoading] = useState(false);
  const [editingWstgItem, setEditingWstgItem] = useState(null);
  const [wstgItemNotes, setWstgItemNotes] = useState('');
  const [showLinkFindingDialog, setShowLinkFindingDialog] = useState(false);
  const [linkFindingItem, setLinkFindingItem] = useState(null);
  const [availableFindings, setAvailableFindings] = useState([]);
  const [findingsLoading, setFindingsLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [showDeleteNoteDialog, setShowDeleteNoteDialog] = useState(false);
  const [newNote, setNewNote] = useState({ title: '', content: '' });

  const openEntityOverview = useCallback(async (entityType, entityId, entityData = null) => {
    if (!currentProject?.id || !entityId) return;
    
    setEntityDetails(entityData);
    setSelectedEntity({ type: entityType, id: entityId });
    setOverviewLoading(true);
    setEntityOverview(null);
    setSidePanelTab('overview');
    
    try {
      const entityTypeMap = {
        'host': 'hosts',
        'domain': 'domains',
        'port': 'ports',
        'service': 'ports',
        'url': 'urls',
        'finding': 'findings'
      };
      
      let apiEntityType = entityTypeMap[entityType];
      
      if (!apiEntityType) {
        if (entityType === 'webapp') {
          apiEntityType = 'urls';
        } else {
          apiEntityType = entityType + 's';
        }
      }
      const response = await api.get(
        `/projects/${currentProject.id}/results/${apiEntityType}/${entityId}/overview`
      );
      
      setEntityOverview(response.data);
      markAsSeen(entityType, entityId);
    } catch (error) {
      console.error(`Error fetching ${entityType} overview:`, error);
      toaster.danger(`Failed to load ${entityType} overview`);
      setSelectedEntity(null);
      setEntityDetails(null);
    } finally {
      setOverviewLoading(false);
    }
  }, [currentProject?.id, markAsSeen]);

  const closeEntityOverview = () => {
    setSelectedEntity(null);
    setEntityOverview(null);
    setEntityDetails(null);
    setSidePanelTab('overview');
    setNotes([]);
    setSelectedNote(null);
    setIsEditingNote(false);
    setNewNote({ title: '', content: '' });
    setWstgChecklist(null);
    setEditingWstgItem(null);
    setWstgItemNotes('');
  };

  const fetchWstgChecklist = useCallback(async () => {
    if (!currentProject?.id || !selectedEntity) return;
    
    if (selectedEntity.type !== 'host' && selectedEntity.type !== 'domain') {
      return;
    }
    
    try {
      setWstgLoading(true);
      const params = new URLSearchParams();
      
      if (selectedEntity.type === 'host') {
        params.append('host_id', selectedEntity.id);
      } else if (selectedEntity.type === 'domain') {
        params.append('domain_id', selectedEntity.id);
      }
      
      const response = await api.get(
        `/projects/${currentProject.id}/wstg-checklist?${params.toString()}`
      );
      
      setWstgChecklist(response.data);
    } catch (error) {
      console.error('Error fetching WSTG checklist:', error);
      toaster.danger('Failed to load WSTG checklist');
    } finally {
      setWstgLoading(false);
    }
  }, [currentProject?.id, selectedEntity]);

  useEffect(() => {
    if (sidePanelTab === 'wstg' && selectedEntity && !wstgChecklist && !wstgLoading) {
      fetchWstgChecklist();
    }
  }, [sidePanelTab, selectedEntity, wstgChecklist, wstgLoading, fetchWstgChecklist]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const returnToWstg = searchParams.get('returnToWstg');
    const entityType = searchParams.get('entityType');
    const entityId = searchParams.get('entityId');
    
    if (returnToWstg === 'true' && entityType && entityId) {
      navigate(location.pathname, { replace: true });
      
      setTimeout(() => {
        openEntityOverview(entityType, entityId);
        setSidePanelTab('wstg');
      }, 200);
    }
  }, [location.search, navigate, location.pathname, openEntityOverview]);

  const fetchAvailableFindings = useCallback(async () => {
    if (!currentProject?.id || !selectedEntity) return;
    
    try {
      setFindingsLoading(true);
      // Use the new filtered endpoint that only returns findings for this domain/host
      const params = new URLSearchParams();
      if (selectedEntity.type === 'host') {
        params.append('host_id', selectedEntity.id);
      } else if (selectedEntity.type === 'domain') {
        params.append('domain_id', selectedEntity.id);
      } else {
        // For other types, fall back to all findings
        const response = await api.get(`/projects/${currentProject.id}/results/findings?limit=1000`);
        setAvailableFindings(response.data.findings || []);
        return;
      }
      
      const response = await api.get(
        `/projects/${currentProject.id}/wstg-checklist/available-findings?${params.toString()}`
      );
      setAvailableFindings(response.data.findings || []);
    } catch (error) {
      console.error('Error fetching findings:', error);
      toaster.danger('Failed to load findings');
    } finally {
      setFindingsLoading(false);
    }
  }, [currentProject?.id, selectedEntity]);

  const handleOpenLinkFinding = async (item) => {
    setLinkFindingItem(item);
    setShowLinkFindingDialog(true);
    await fetchAvailableFindings();
  };

  const handleLinkFinding = async (findingId) => {
    if (!linkFindingItem) {
      toaster.danger('No item selected for linking');
      return;
    }
    
    try {
      let updatedItem;
      
      // If item doesn't have an id yet (placeholder), we need to create it first via the main endpoint
      if (!linkFindingItem.id) {
        // Create the item first with the finding linked
        const createData = {
          test_id: linkFindingItem.test_id,
          test_name: linkFindingItem.test_name,
          category: linkFindingItem.category,
          status: linkFindingItem.status || null,
          notes: linkFindingItem.notes || null,
          host_id: selectedEntity?.type === 'host' ? selectedEntity.id : null,
          domain_id: selectedEntity?.type === 'domain' ? selectedEntity.id : null,
        };
        
        // Create item via main endpoint
        const createResponse = await api.post(
          `/projects/${currentProject.id}/wstg-checklist`,
          createData
        );
        
        // Now link the finding
        const linkResponse = await api.post(
          `/projects/${currentProject.id}/wstg-checklist/${createResponse.data.id}/link-finding/${findingId}`
        );
        updatedItem = linkResponse.data;
      } else {
        // Item exists, just link the finding
        const response = await api.post(
          `/projects/${currentProject.id}/wstg-checklist/${linkFindingItem.id}/link-finding/${findingId}`
        );
        updatedItem = response.data;
      }
      
      // Optimistically update local state with the returned item
      setWstgChecklist(prev => {
        if (!prev || !prev.items) return prev;
        return {
          ...prev,
          items: prev.items.map(item => 
            item.test_id === linkFindingItem.test_id ? updatedItem : item
          )
        };
      });
      
      setShowLinkFindingDialog(false);
      setLinkFindingItem(null);
      toaster.success('Finding linked successfully');
    } catch (error) {
      console.error('Error linking finding:', error);
      if (error.response?.status === 400) {
        const detail = error.response?.data?.detail || '';
        if (detail.includes('already linked')) {
          toaster.warning('Finding is already linked to this item');
        } else if (detail.includes('not associated')) {
          toaster.warning('Finding is not associated with this domain/host/port');
        } else {
          toaster.danger(detail || 'Failed to link finding');
        }
      } else {
        toaster.danger('Failed to link finding');
      }
    }
  };

  const handleUnlinkFinding = async (item, findingId = null) => {
    if (!item || !item.id) return;
    
    try {
      let updatedItem;
      
      // If findingId is provided, use the new many-to-many endpoint
      if (findingId) {
        const response = await api.delete(
          `/projects/${currentProject.id}/wstg-checklist/${item.id}/unlink-finding/${findingId}`
        );
        updatedItem = response.data;
      } else {
        // Fallback to old endpoint for backward compatibility
        const response = await api.put(
          `/projects/${currentProject.id}/wstg-checklist/${item.id}/unlink-finding`
        );
        updatedItem = response.data;
      }
      
      // Optimistically update local state
      setWstgChecklist(prev => {
        if (!prev || !prev.items) return prev;
        return {
          ...prev,
          items: prev.items.map(i => 
            i.test_id === item.test_id ? updatedItem : i
          )
        };
      });
      
      toaster.success('Finding unlinked successfully');
    } catch (error) {
      console.error('Error unlinking finding:', error);
      toaster.danger('Failed to unlink finding');
    }
  };

  const handleOpenCreateFinding = (item) => {
    const params = new URLSearchParams();
    params.append('returnToWstg', 'true');
    params.append('entityType', selectedEntity.type);
    params.append('entityId', selectedEntity.id);
    params.append('testId', item.test_id);
    
    const state = {
      host_id: selectedEntity.type === 'host' ? selectedEntity.id : null,
      domain_id: selectedEntity.type === 'domain' ? selectedEntity.id : null,
      test_id: item.test_id,
      test_name: item.test_name
    };
    
    navigate(`/create-finding?${params.toString()}`, { state });
  };

  const fetchNotes = useCallback(async () => {
    if (!currentProject?.id || !selectedEntity) return;
    
    try {
      setNotesLoading(true);
      const params = new URLSearchParams();
      
      if (selectedEntity.type === 'host' && selectedEntity.id) {
        params.append('host_id', selectedEntity.id);
      } else if (selectedEntity.type === 'domain' && selectedEntity.id) {
        params.append('domain_id', selectedEntity.id);
      } else if (selectedEntity.type === 'port' || selectedEntity.type === 'service') {
        params.append('port_id', selectedEntity.id);
      } else if (selectedEntity.type === 'url' || selectedEntity.type === 'webapp') {
        setNotesLoading(false);
        return;
      }
      
      if (params.toString()) {
        const response = await api.get(
          `/projects/${currentProject.id}/notes?${params.toString()}`
        );
        setNotes(response.data);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
      toaster.danger('Failed to fetch notes');
    } finally {
      setNotesLoading(false);
    }
  }, [currentProject?.id, selectedEntity]);

  useEffect(() => {
    if (sidePanelTab === 'notes' && selectedEntity) {
      fetchNotes();
    }
  }, [sidePanelTab, selectedEntity, fetchNotes]);

  const handleCreateNote = async () => {
    if (!currentProject?.id || !selectedEntity) return;
    
    if (!newNote.title.trim() || !newNote.content.trim()) {
      toaster.warning('Please provide both title and content');
      return;
    }
    
    try {
      const noteData = {
        title: newNote.title,
        content: newNote.content,
        ...(selectedEntity.type === 'host' && { host_id: selectedEntity.id }),
        ...(selectedEntity.type === 'domain' && { domain_id: selectedEntity.id }),
        ...((selectedEntity.type === 'port' || selectedEntity.type === 'service') && { port_id: selectedEntity.id })
      };
      
      const response = await api.post(
        `/projects/${currentProject.id}/notes`,
        noteData
      );
      setNotes([...notes, response.data]);
      setNewNote({ title: '', content: '' });
      setIsEditingNote(false);
      toaster.success('Note created successfully');
      fetchNotes();
    } catch (error) {
      console.error('Error creating note:', error);
      toaster.danger('Failed to create note');
    }
  };

  const handleUpdateNote = async () => {
    if (!currentProject?.id || !selectedNote) return;
    
    try {
      const response = await api.put(
        `/projects/${currentProject.id}/notes/${selectedNote.id}`,
        selectedNote
      );
      setNotes(notes.map(note => 
        note.id === selectedNote.id ? response.data : note
      ));
      setIsEditingNote(false);
      setSelectedNote(null);
      toaster.success('Note updated successfully');
      fetchNotes();
    } catch (error) {
      console.error('Error updating note:', error);
      toaster.danger('Failed to update note');
    }
  };

  const handleDeleteNote = async () => {
    if (!currentProject?.id || !selectedNote) return;
    
    try {
      await api.delete(`/projects/${currentProject.id}/notes/${selectedNote.id}`);
      setNotes(notes.filter(note => note.id !== selectedNote.id));
      setSelectedNote(null);
      setShowDeleteNoteDialog(false);
      toaster.success('Note deleted successfully');
      fetchNotes();
    } catch (error) {
      console.error('Error deleting note:', error);
      toaster.danger('Failed to delete note');
    }
  };

  const markUrlAsSeen = async (urlId) => {
    if (!currentProject?.id || !urlId) return;
    
    try {
      await api.post(`/projects/${currentProject.id}/results/urls/${urlId}/mark-seen`);
      markAsSeen('url', urlId);
      toaster.success('URL marked as seen');
    } catch (error) {
      console.error('Error marking URL as seen:', error);
      toaster.danger('Failed to mark URL as seen');
    }
  };

  const markAllUrlsAsSeen = async (urls, userOverride = null) => {
    // Use userOverride if provided, otherwise fall back to currentUser from context
    const user = userOverride || currentUser;
    
    if (!currentProject?.id || !urls || urls.length === 0 || !user?.id) {
      console.warn('markAllUrlsAsSeen: Invalid parameters', { 
        hasProject: !!currentProject?.id, 
        urlsCount: urls?.length, 
        hasUser: !!user?.id,
        userOverride: !!userOverride,
        currentUserFromContext: !!currentUser
      });
      return { success: false, message: 'Invalid parameters' };
    }
    
    try {
      const unseenUrls = urls.filter(url => !url.seen_by_user_id || (user?.id && url.seen_by_user_id !== user.id));
      
      if (unseenUrls.length === 0) {
        toaster.warning('All URLs are already marked as seen');
        return { success: true, message: 'All URLs already seen' };
      }
      
      console.log(`markAllUrlsAsSeen: Marking ${unseenUrls.length} URLs as seen`);
      
      // Use Promise.allSettled to handle partial failures
      const results = await Promise.allSettled(
        unseenUrls.map(url => {
          console.log(`markAllUrlsAsSeen: POST /projects/${currentProject.id}/results/urls/${url.id}/mark-seen`);
          return api.post(`/projects/${currentProject.id}/results/urls/${url.id}/mark-seen`);
        })
      );
      
      // Count successful and failed requests
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      
      // Update local state for successful ones
      // Note: We don't call markAsSeen here because it would make duplicate API calls
      // The API calls are already done above, we just need to update the UI state
      // The refreshEntityOverview call will update the entityOverview state
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          // URL is already marked on the backend, we'll refresh the overview to get updated data
          // No need to call markAsSeen as it would make another API call
        } else {
          console.error(`Failed to mark URL ${unseenUrls[index].id} as seen:`, result.reason);
        }
      });
      
      if (failed === 0) {
        toaster.success(`${successful} URL(s) marked as seen`);
        return { success: true, count: successful };
      } else if (successful > 0) {
        toaster.warning(`${successful} URL(s) marked as seen, ${failed} failed`);
        return { success: true, count: successful, failed };
      } else {
        toaster.danger('Failed to mark URLs as seen');
        return { success: false, message: 'All requests failed' };
      }
    } catch (error) {
      console.error('Error marking URLs as seen:', error);
      toaster.danger('Failed to mark URLs as seen');
      return { success: false, error };
    }
  };

  const refreshEntityOverview = useCallback(async () => {
    if (!currentProject?.id || !selectedEntity) return;
    
    try {
      const entityTypeMap = {
        'host': 'hosts',
        'domain': 'domains',
        'port': 'ports',
        'service': 'ports',
        'url': 'urls',
        'finding': 'findings'
      };
      
      let apiEntityType = entityTypeMap[selectedEntity.type];
      
      if (!apiEntityType) {
        if (selectedEntity.type === 'webapp') {
          apiEntityType = 'urls';
        } else {
          apiEntityType = selectedEntity.type + 's';
        }
      }
      
      const response = await api.get(
        `/projects/${currentProject.id}/results/${apiEntityType}/${selectedEntity.id}/overview`
      );
      
      setEntityOverview(response.data);
    } catch (error) {
      console.error('Error refreshing entity overview:', error);
    }
  }, [currentProject?.id, selectedEntity]);

  return {
    selectedEntity,
    entityOverview,
    setEntityOverview,
    overviewLoading,
    entityDetails,
    sidePanelTab,
    setSidePanelTab,
    wstgChecklist,
    wstgLoading,
    editingWstgItem,
    setEditingWstgItem,
    wstgItemNotes,
    setWstgItemNotes,
    showLinkFindingDialog,
    setShowLinkFindingDialog,
    linkFindingItem,
    availableFindings,
    findingsLoading,
    notes,
    notesLoading,
    selectedNote,
    setSelectedNote,
    isEditingNote,
    setIsEditingNote,
    showDeleteNoteDialog,
    setShowDeleteNoteDialog,
    newNote,
    setNewNote,
    openEntityOverview,
    closeEntityOverview,
    fetchWstgChecklist,
    handleOpenLinkFinding,
    handleLinkFinding,
    handleUnlinkFinding,
    handleOpenCreateFinding,
    handleCreateNote,
    handleUpdateNote,
    handleDeleteNote,
    markUrlAsSeen,
    markAllUrlsAsSeen,
    refreshEntityOverview,
    updateWstgItem: (testId, updates) => {
      setWstgChecklist(prev => {
        if (!prev || !prev.items) return prev;
        return {
          ...prev,
          items: prev.items.map(item => 
            item.test_id === testId ? { ...item, ...updates } : item
          )
        };
      });
    }
  };
};

